/*
 * The Belgian Brewer: scroll motion with GSAP + ScrollTrigger (progressive enhancement).
 * Sections that want motion carry data-tbb-motion and data-tbb-gsap / data-tbb-st (asset URLs);
 * GSAP is only fetched on those pages, once, after the page has loaded. Without it every section
 * is complete and static. Nothing moves under prefers-reduced-motion.
 *   - Visit: at 900px+ the heading column pins beside the venues; the index row of the venue in view lights up.
 *   - Beers: at 1100px+ the map plate pins while the index scrolls, if the whole plate fits the screen.
 *   - Story: paragraphs marked data-tbb-scrub reveal word by word (opacity .18 -> 1) as they scroll through.
 * Horizon scrolls .page-wrapper at 990px+ and the document below that, so the scroller follows suit.
 */
import { getScrollContainer } from '@theme/scroll-container';

const CLEAR = 112; // floating header (12px inset + band) plus breathing room
// Visit heading column: a little below the floating header's bottom edge (falls back to CLEAR)
const sideClear = () => {
  const bottom = document.querySelector('header')?.getBoundingClientRect().bottom;
  return bottom > 0 && bottom < CLEAR * 1.5 ? Math.round(bottom + 28) : CLEAR;
};

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-tbb-src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded) resolve();
      else existing.addEventListener('load', resolve, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.dataset.tbbSrc = src;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
}

function wrapWords(element) {
  if (element.dataset.tbbWrapped) return;
  element.dataset.tbbWrapped = 'true';
  [...element.childNodes].forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const fragment = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }
        const span = document.createElement('span');
        span.className = 'tbb-w';
        span.textContent = part;
        fragment.appendChild(span);
      });
      node.replaceWith(fragment);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      wrapWords(node);
    }
  });
}

let context = null;

function build() {
  const { gsap, ScrollTrigger } = window;
  if (!gsap || !ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  context?.revert();
  context = gsap.matchMedia();

  context.add(
    {
      visit: '(min-width: 900px)',
      map: '(min-width: 1100px)',
      narrow: '(max-width: 899px)',
      squeeze: '(min-width: 990px)',
      reduce: '(prefers-reduced-motion: reduce)',
    },
    (ctx) => {
      const { visit, map, narrow, reduce } = ctx.conditions;
      if (reduce) return;

      const container = getScrollContainer();
      const scroller = container === document.scrollingElement || container === document.documentElement ? window : container;
      ScrollTrigger.defaults({ scroller });

      // Visit: the heading column holds while both venues scroll past
      if (visit) {
        document.querySelectorAll('[data-tbb-visit]').forEach((split) => {
          const side = split.querySelector('[data-tbb-visit-side]');
          if (!side) return;
          // Pin by the column's own top (not the split's padded top) so the heading sits just under the header
          ScrollTrigger.create({
            trigger: side,
            endTrigger: split,
            pin: side,
            start: () => `top top+=${sideClear()}`,
            end: () => `bottom top+=${sideClear() + side.offsetHeight}`,
            pinSpacing: false,
            invalidateOnRefresh: true,
          });
          split.querySelectorAll('[data-tbb-venue]').forEach((venue) => {
            const row = split.querySelector(`[data-tbb-index="${venue.dataset.tbbVenue}"]`);
            if (!row) return;
            ScrollTrigger.create({
              trigger: venue,
              start: 'top 60%',
              end: 'bottom 60%',
              toggleClass: { targets: row, className: 'is-current' },
            });
          });
        });
      }

      // Beers: the map plate holds while the index scrolls, only when the whole plate fits
      if (map) {
        document.querySelectorAll('[data-tbb-beers]').forEach((section) => {
          const atlas = section.querySelector('.tbb-beers__atlas');
          const plate = section.querySelector('.tbb-beers__plate');
          if (!atlas || !plate || plate.offsetHeight + CLEAR > window.innerHeight - 8) return;
          ScrollTrigger.create({
            trigger: atlas,
            pin: plate,
            start: () => `top top+=${CLEAR}`,
            end: () => `bottom top+=${CLEAR + plate.offsetHeight}`,
            pinSpacing: false,
            invalidateOnRefresh: true,
          });
        });
      }

      // Story: word-by-word reveal
      document.querySelectorAll('[data-tbb-scrub-group]').forEach((group) => {
        group.querySelectorAll('[data-tbb-scrub]').forEach(wrapWords);
        gsap.fromTo(
          group.querySelectorAll('.tbb-w'),
          { opacity: 0.18 },
          {
            opacity: 1,
            ease: 'none',
            stagger: 0.05,
            scrollTrigger: {
              trigger: group,
              start: narrow ? 'top 88%' : 'top 78%',
              end: narrow ? 'bottom 62%' : 'bottom 52%',
              scrub: narrow ? 0.5 : 0.6,
            },
          }
        );
      });
    }
  );

  const refresh = () => ScrollTrigger.refresh();
  document.fonts?.ready.then(refresh);
  window.addEventListener('load', refresh, { once: true });

  // content that changes height (event filter, enquiry form) moves everything below it
  if (!document.documentElement.dataset.tbbMotionClicks) {
    document.documentElement.dataset.tbbMotionClicks = 'true';
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-tbb-filter], [data-tbb-hire] form button, .header-menu, header-drawer')) {
        setTimeout(() => window.ScrollTrigger?.refresh(), 80);
      }
    });
  }
}

async function start() {
  const source = document.querySelector('[data-tbb-motion][data-tbb-gsap]');
  if (!source) return;
  try {
    await loadScript(source.dataset.tbbGsap);
    await loadScript(source.dataset.tbbSt);
  } catch {
    return; // the page is complete without motion
  }
  build();
}

if (document.readyState === 'complete') start();
else window.addEventListener('load', start, { once: true });

// Theme editor: rebuild when sections are added, changed or removed
['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) =>
  document.addEventListener(type, () => {
    if (window.gsap && window.ScrollTrigger) setTimeout(build, 60);
    else start();
  })
);
